/* eslint-disable import/no-named-as-default-member */
import create from 'zustand';
import tradeAPI from 'lib/api/trade';
import alertAPI from 'lib/api/alert';
import { postStopOrder, editStopOrder } from 'lib/api/smartOrder';
import {
  ReqCreateStopOrder,
  ReqAmendStopOrder,
} from 'global/Securities/types/api/smartOrder';

import { postCancel } from 'global/VirtualTrading/api/config';
import { VirtualExpiryId } from 'global/VirtualTrading/types/transactions';
import { VirtualCancelRequestBody } from 'global/VirtualTrading/types/api/cancel';
import {
  DayTradeBuyPayload,
  DayTradeSellPayload,
  DayTradeAmendPayload,
  BulkTradeAmendPayload,
} from 'global/Securities/types/trading';

interface iDefaultData {
  isLoading?: boolean;
  message?: string;
  data?: any;
  error?: string;
}

const defaultData = {
  isLoading: false,
  message: '',
  data: null,
  error: '',
};

interface iState {
  postTradeCancelData: iDefaultData;
  addPriceAlertData: iDefaultData;
  postTradeBuyData: iDefaultData;
  postSmartOrderBuyData: iDefaultData;
  postTradeSellData: iDefaultData;
  postTradeAmendData: iDefaultData;
  postRequestExerciseData: iDefaultData;

  postDayTradeBuyData: iDefaultData;
  postDayTradeSellData: iDefaultData;
  postDayTradeAmendData: iDefaultData;
  postDayTradeCancelData: iDefaultData;

  deleteTrailingStopData: iDefaultData;
  deleteStopOrderData: iDefaultData;
  deleteBracketOrderData: iDefaultData;
}

interface ITokenStore extends iState {
  postTradeCancel: (any) => Promise<iDefaultData>;
  addPriceAlert: (any) => Promise<iDefaultData>;
  postTradeBuy: (any) => Promise<iDefaultData>;
  postSmartOrder: (payload: ReqCreateStopOrder) => Promise<iDefaultData>;
  postTradeSell: (any) => Promise<iDefaultData>;
  postTradeAmend: (payload: BulkTradeAmendPayload) => Promise<iDefaultData>;
  postRequestExercise: (any) => Promise<iDefaultData>;
  editSmartOrder: (
    orderId: string,
  ) => (payload: ReqAmendStopOrder) => Promise<iDefaultData>;

  postDayTradeBuy: (payload: DayTradeBuyPayload) => Promise<iDefaultData>;
  postDayTradeSell: (payload: DayTradeSellPayload) => Promise<iDefaultData>;
  postDayTradeAmend: (payload: DayTradeAmendPayload) => Promise<iDefaultData>;
  postDayTradeCancel: (orderKey: string) => Promise<iDefaultData>;

  deleteTrailingStop: (orderId: string) => Promise<iDefaultData>;
  deleteStopOrder: (orderId: string) => Promise<iDefaultData>;
  deleteBracketOrder: (orderId: string) => Promise<iDefaultData>;
}

const setIsLoading = (key, set) => {
  set({
    [key]: {
      ...defaultData,
      isLoading: true,
    },
  });
};

const setError = (error, key, set) => {
  set({
    [key]: {
      ...defaultData,
      error,
    },
  });
};

const setData = (data, message, key, set) => {
  set({
    [key]: {
      ...defaultData,
      data,
      message,
    },
  });
};

const tradingOperationStore = create<ITokenStore>((set) => ({
  postTradeCancelData: defaultData,
  addPriceAlertData: defaultData,
  postTradeBuyData: defaultData,
  postSmartOrderBuyData: defaultData,
  postTradeSellData: defaultData,
  postTradeAmendData: defaultData,
  postRequestExerciseData: defaultData,

  postDayTradeBuyData: defaultData,
  postDayTradeSellData: defaultData,
  postDayTradeAmendData: defaultData,
  postDayTradeCancelData: defaultData,

  deleteTrailingStopData: defaultData,
  deleteStopOrderData: defaultData,
  deleteBracketOrderData: defaultData,

  postTradeCancel: async ({
    orderid,
    orderkey,
    symbol,
    gtc,
    isRealTrading,
  }) => {
    const key = 'postTradeCancelData';
    setIsLoading(key, set);
    try {
      const apiData = isRealTrading
        ? { orderid, orderkey, symbol, gtc }
        : ({
            order_id: orderid,
            gtc: gtc === VirtualExpiryId.GoodTillCanceled,
          } as VirtualCancelRequestBody);

      const apiFunction = !isRealTrading ? postCancel : tradeAPI.tradeCancel;
      const response = await apiFunction(apiData);

      if (!response.data) {
        setError('Attempt to cancel trade failed', key, set);
        throw new Error('Attempt to cancel trade failed');
      }
      const { data, message } = response.data;
      set({
        postTradeCancelData: {
          ...defaultData,
          data,
          message,
        },
      });
      setData(data, message, key, set);
      return { data, message };
    } catch (error) {
      setError(error, key, set);
      return error;
    }
  },
  addPriceAlert: async ({ name, command }) => {
    const key = 'addPriceAlertData';
    setIsLoading(key, set);
    try {
      const response = await alertAPI.addPriceAlert(name, command);

      if (!response.data) {
        const error = 'Attempt to retrieve exercise list failed';
        setError(error, key, set);
        throw new Error(error);
      }
      const { data, message } = response.data;
      setData(data, message, key, set);
      return { data, message };
    } catch (error) {
      setError(error, key, set);
      return { error };
    }
  },

  postTradeBuy: async (buyData) => {
    const key = 'postTradeBuyData';
    setIsLoading(key, set);
    try {
      const response = await tradeAPI.tradeBuy(buyData);

      if (!response.data) {
        const error = 'Attempt to post trade buy failed';
        setError(error, key, set);
        throw new Error(error);
      }
      const { data, message } = response.data;
      setData(data, message, key, set);
      return { data, message };
    } catch (error) {
      setError(error, key, set);
      return { error };
    }
  },

  postSmartOrder: async (payload) => {
    const key = 'postSmartOrderBuyData';
    setIsLoading(key, set);
    try {
      const response = await postStopOrder(payload);

      if (!response.data) {
        const error = 'Attempt to post smart order failed';
        setError(error, key, set);
        throw new Error(error);
      }
      const { data, message } = response.data;
      setData(data, message, key, set);
      return { data, message };
    } catch (error) {
      setError(error, key, set);
      return { error };
    }
  },

  postTradeSell: async (sellData) => {
    const key = 'postTradeSellData';
    setIsLoading(key, set);
    try {
      const response = await tradeAPI.tradeSell(sellData);

      if (!response.data) {
        const error = 'Attempt to post trade sell failed';
        setError(error, key, set);
        throw new Error(error);
      }
      const { data, message } = response.data;
      setData(data, message, key, set);
      return { data, message };
    } catch (error) {
      setError(error, key, set);
      return { error };
    }
  },

  postTradeAmend: async (orders) => {
    const key = 'postTradeAmendData';
    setIsLoading(key, set);
    try {
      const response = await tradeAPI.bulkTradeAmend(orders);

      if (!response.data) {
        const error = 'Attempt to post trade ammend failed';
        setError(error, key, set);
        throw new Error(error);
      }
      const { data, message } = response.data;

      setData(data, message, key, set);
      return { data, message };
    } catch (error) {
      setError(error, key, set);
      return { error };
    }
  },

  postRequestExercise: async ({ orderKey, shares, stockCode }) => {
    const key = 'postRequestExerciseData';
    setIsLoading(key, set);
    try {
      const response = await tradeAPI.postRequestExercise({
        stockCode,
        shares,
        orderKey,
      });

      if (!response.data) {
        const error = 'Attempt to post request exercise data failed';
        setError(error, key, set);
        throw new Error(error);
      }
      const { data, message } = response.data;

      setData(data, message, key, set);
      return { data, message };
    } catch (error) {
      setError(error, key, set);
      return { error };
    }
  },

  editSmartOrder: (orderId) => async (payload) => {
    try {
      const response = await editStopOrder(orderId)(payload);
      if (!response.data) {
        throw new Error('Error while fetching data');
      }

      const { data, error, error_type: errorType, message } = response.data;

      if (error || errorType) {
        throw new Error(message);
      }

      return { data, message };
    } catch (error) {
      return { error };
    }
  },

  // Day Trade
  postDayTradeBuy: async ({ symbol, price, shares, multiplier, platform_order_type }) => {
    const key = 'postDayTradeBuyData';
    setIsLoading(key, set);
    try {
      const API = tradeAPI;
      const response = await API.dayTradeBuy({
        symbol,
        price,
        shares,
        multiplier,
        platform_order_type,
      });

      if (!response.data) {
        const error = 'Attempt to post day trade buy failed';
        setError(error, key, set);
        throw new Error(error);
      }
      const { data, message } = response.data;
      setData(data, message, key, set);
      return { data, message };
    } catch (error) {
      setError(error, key, set);
      return { error };
    }
  },

  postDayTradeSell: async ({ symbol, price, shares, platform_order_type }) => {
    const key = 'postDayTradeSellData';
    setIsLoading(key, set);
    try {
      const API = tradeAPI;
      const response = await API.dayTradeSell({
        symbol,
        price,
        shares,
        platform_order_type,
      });

      if (!response.data) {
        const error = 'Attempt to post day trade sell failed';
        setError(error, key, set);
        throw new Error(error);
      }
      const { data, message } = response.data;
      setData(data, message, key, set);
      return { data, message };
    } catch (error) {
      setError(error, key, set);
      return { error };
    }
  },

  postDayTradeAmend: async ({ orderid, price, shares, multiplier }) => {
    const key = 'postDayTradeAmendData';
    setIsLoading(key, set);
    try {
      const response = await tradeAPI.dayTradeAmend({
        orderid,
        price,
        shares,
        multiplier,
      });

      if (!response.data) {
        const error = 'Attempt to post day trade ammend failed';
        setError(error, key, set);
        throw new Error(error);
      }
      const { data, message } = response.data;

      setData(data, message, key, set);
      return { data, message };
    } catch (error) {
      setError(error, key, set);
      return { error };
    }
  },

  postDayTradeCancel: async (orderKey) => {
    const key = 'postDayTradeCancelData';
    setIsLoading(key, set);
    try {
      const response = await tradeAPI.dayTradeCancel(orderKey);

      if (!response.data) {
        setError('Attempt to cancel day trade failed', key, set);
        throw new Error('Attempt to cancel day trade failed');
      }
      const { data, message } = response.data;
      set({
        postDayTradeCancelData: {
          ...defaultData,
          data,
          message,
        },
      });
      setData(data, message, key, set);
      return { data, message };
    } catch (error) {
      setError(error, key, set);
      return error;
    }
  },

  deleteTrailingStop: async (orderId) => {
    const key = 'deleteTrailingStopData';

    try {
      const response = await tradeAPI.deleteTrailingStop(orderId);

      if (!response.data) {
        setError('Attempt to delete trailing stop failed', key, set);
        throw new Error('Attempt to delete trailing stop failed');
      }

      const { data, message } = response.data;
      setData(data, message, key, set);
      return { data, message };
    } catch (e) {
      setError(e, key, set);
      return e;
    }
  },
  deleteStopOrder: async (orderId) => {
    const key = 'deleteStopOrderData';
    try {
      const response = await tradeAPI.deleteStopOrder(orderId);

      if (!response.data) {
        setError('Attempt to cancel stop order failed', key, set);
        throw new Error('Attempt to cancel stop order failed');
      }

      const { data, message } = response.data;
      setData(data, message, key, set);
      return { data, message };
    } catch (e) {
      setError(e, key, set);
      return e;
    }
  },
  deleteBracketOrder: async (orderId) => {
    const key = 'deleteBracketOrderData';
    try {
      const response = await tradeAPI.deleteBracketOrder(orderId);

      if (!response.data) {
        setError('Attempt to cancel bracket order failed', key, set);
        throw new Error('Attempt to cancel bracket order failed');
      }

      const { data, message } = response.data;
      setData(data, message, key, set);
      return { data, message };
    } catch (e) {
      setError(e, key, set);
      return e;
    }
  },
}));

export default tradingOperationStore;
